import { useState } from 'react';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { getPlayerChatPageUrl } from 'constants/pages';
import {
  AcceptPlayRequestResponse,
  DeclinePlayRequestPayload,
  DeclinePlayRequestResponse,
  MAX_PLAY_REQUEST_MESSAGE_LENGTH,
  getPlayRequestAcceptApi,
  getPlayRequestDeclineApi,
} from 'constants/payloads/playRequests';
import { useApiGateway } from 'hooks/useApi';
import CheckCircle from 'svg/CheckCircle';
import CloseIcon from 'svg/CloseIcon';
import InfoCircle from 'svg/InfoCircle';
import { Button } from 'components/Button';
import { NeighborhoodList } from 'components/Find/Neighborhoods';
import Success3dIllustration from 'components/Find/Success3dIllustration';
import VenueDetailCard from 'components/Find/detail/VenueDetailCard';
import SportBadge from 'components/SportBadge';
import Modal from 'components/modals/Modal';
import CloseButton from './CloseButton';
import RequesterInfo from './RequesterInfo';
import Warning3dIllustration from './Warning3dIllustration';
import type { HostPlayRequest } from './types';

interface Props {
  request: HostPlayRequest | null;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful accept/decline so the parent can refetch. */
  onResolved: () => void;
}

type Step = 'review' | 'accept' | 'decline' | 'done';

const DEFAULT_DECLINE_MESSAGE =
  "Thanks for reaching out. I'm not able to play with you this time, but I hope we can play another time.";

/**
 * A host's review of one incoming play request: see the requester + message (and,
 * for availability requests, their availability + chosen court), then Accept or
 * Decline through a confirmation step to a "Done!" state. Accept/decline run
 * through the API routes (conversation formation + auto-cancel happen server-side).
 */
export default function RequestReviewModal({ request, isOpen, onClose, onResolved }: Props) {
  const router = useRouter();
  const [step, setStep] = useState<Step>('review');
  const [declineMessage, setDeclineMessage] = useState(DEFAULT_DECLINE_MESSAGE);
  // Set on a successful accept so the "Done!" step can offer the new thread.
  const [conversationId, setConversationId] = useState<string | null>(null);

  const { post: acceptPost, isLoading: isAccepting } = useApiGateway<
    undefined,
    AcceptPlayRequestResponse
  >();
  const { post: declinePost, isLoading: isDeclining } = useApiGateway<
    DeclinePlayRequestPayload,
    DeclinePlayRequestResponse
  >();

  // Safe to reset synchronously: Modal renders a snapshot of its children through
  // the exit transition, so the wiped step never shows on the way out.
  const handleClose = () => {
    onClose();
    setStep('review');
    setDeclineMessage(DEFAULT_DECLINE_MESSAGE);
    setConversationId(null);
  };

  const handleAccept = async () => {
    if (!request) return;
    const response = await acceptPost({ endpoint: getPlayRequestAcceptApi(request.id) });
    if (response.isError) {
      toast.error(response.error || 'Could not accept the request');
      return;
    }
    // The accept route forms the conversation and hands back its id. `data` is
    // typed loosely on the gateway (it also carries streamed text), hence the cast.
    const accepted = response.data as AcceptPlayRequestResponse | undefined;
    setConversationId(accepted?.conversationId ?? null);
    onResolved();
    setStep('done');
  };

  const handleDecline = async () => {
    if (!request) return;
    const response = await declinePost({
      endpoint: getPlayRequestDeclineApi(request.id),
      payload: { responseMessage: declineMessage.trim() || undefined },
    });
    if (response.isError) {
      toast.error(response.error || 'Could not decline the request');
      return;
    }
    onResolved();
    setStep('done');
  };

  const acceptCopy =
    request?.variant === 'GAME'
      ? 'If you accept, the player will be added to your game and added to your event chat.'
      : 'Accept the request to begin a conversation with the player.';

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      classNameMaxWidth="max-w-md lg:max-w-[40rem]"
      classNameRounded="rounded-t-3xl sm:rounded-2xl lg:rounded-3xl"
      classNameHeight="max-h-[90dvh]"
    >
      <div className="flex max-h-[90dvh] flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
        {step === 'review' && request && (
          <>
            <header className="flex shrink-0 items-center justify-between border-b border-color-border-input-lightmode p-ds-lg dark:border-color-border-input-darkmode lg:px-ds-3xl lg:py-5">
              <h2 className="typography-product-heading-mobile lg:typography-product-heading-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Request
              </h2>
              <CloseButton onClose={handleClose} />
            </header>

            <div className="flex min-h-0 flex-1 flex-col gap-ds-xl overflow-y-auto p-ds-lg lg:gap-ds-3xl lg:p-ds-3xl">
              {/* The listing being asked about, above a rule — games head their own
                  chat/modal, so only availability requests need naming here. */}
              {request.activity && (
                <div className="flex flex-col gap-ds-md border-b border-color-border-input-lightmode pb-ds-xl dark:border-color-border-input-darkmode">
                  <SportBadge sport={request.activity.sport} variant="neutral" size="sm" />
                  <h3 className="typography-product-heading-mobile lg:typography-product-heading-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    {request.activity.title}
                  </h3>
                  {request.availableOn && (
                    <p className="typography-product-subheading-mobile lg:typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                      {request.availableOn.map((slot, slotIndex) => (
                        <span key={slot.id}>
                          {slotIndex > 0 && ', '}
                          {slot.parts.map((part, partIndex) => (
                            <span key={`${slot.id}-${partIndex}`}>
                              {partIndex > 0 && <span className="text-color-text-brand"> • </span>}
                              {part}
                            </span>
                          ))}
                        </span>
                      ))}
                    </p>
                  )}
                </div>
              )}

              {/* From player */}
              <div className="flex flex-col gap-ds-md">
                <h3 className="typography-product-heading-compact-mobile lg:typography-product-heading-compact-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  From player
                </h3>
                <div className="flex items-center justify-between gap-ds-sm">
                  <RequesterInfo
                    player={request.player}
                    subLabel={request.player.username && `@${request.player.username}`}
                    sizeDesktop="lg"
                  />
                  {request.player.username && (
                    <Button
                      variant="secondary"
                      size="sm"
                      isInline
                      onClick={() => router.push(`/${request.player.username}`)}
                    >
                      View profile
                    </Button>
                  )}
                </div>
              </div>

              {request.neighborhoods && request.neighborhoods.length > 0 && (
                <div className="flex flex-col gap-ds-md">
                  <h3 className="typography-product-heading-compact-mobile lg:typography-product-heading-compact-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    Neighborhoods
                  </h3>
                  <NeighborhoodList names={request.neighborhoods} />
                </div>
              )}

              {request.location && (
                <div className="flex flex-col gap-ds-md">
                  <h3 className="typography-product-heading-compact-mobile lg:typography-product-heading-compact-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    Location details
                  </h3>
                  <VenueDetailCard venue={request.location} sizeDesktop="lg" />
                </div>
              )}

              {/* Player's message */}
              {request.message && (
                <div className="flex flex-col gap-ds-sm">
                  <h3 className="typography-product-heading-compact-mobile lg:typography-product-heading-compact-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    Player&rsquo;s message
                  </h3>
                  <p className="typography-product-caption lg:typography-product-body-highlight text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {request.message}
                  </p>
                </div>
              )}
            </div>

            <footer className="flex shrink-0 gap-ds-md border-t border-color-border-input-lightmode px-ds-lg pb-ds-lg pt-ds-lg dark:border-color-border-input-darkmode lg:gap-ds-xl lg:px-ds-3xl lg:py-5">
              <Button
                variant="primary"
                size="lg"
                onClick={() => setStep('decline')}
                iconLeft={<CloseIcon className="h-5 w-5" />}
              >
                Decline
              </Button>
              <Button
                variant="brand"
                size="lg"
                onClick={() => setStep('accept')}
                iconLeft={<CheckCircle className="h-5 w-5" />}
              >
                Accept
              </Button>
            </footer>
          </>
        )}

        {step === 'accept' && (
          <>
            <header className="flex shrink-0 items-center justify-end p-ds-lg lg:px-ds-3xl lg:py-5">
              <CloseButton onClose={handleClose} />
            </header>
            <div className="flex min-h-0 flex-1 flex-col items-center gap-ds-lg overflow-y-auto px-ds-lg pb-ds-2xl pt-ds-sm text-center lg:gap-ds-2xl lg:px-ds-3xl lg:pb-16">
              <img
                src="/images/app/welcome-3d.png"
                alt=""
                className="h-[7.5rem] w-[7.5rem] object-contain"
              />
              {/* `contents` keeps the mobile column flat; a real text block at lg. */}
              <div className="contents lg:flex lg:w-full lg:flex-col lg:items-center lg:gap-ds-xl">
                <h2 className="typography-product-display text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  Accept request
                </h2>
                <p className="typography-product-body max-w-[19.5rem] text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary lg:max-w-none">
                  {acceptCopy}
                </p>
                {/* Only the accept that fills the game auto-declines the rest, so
                    that is the only one that says so. */}
                {request?.isLastSeat && (
                  <div className="flex items-center gap-ds-md rounded-xl bg-color-bg-lightmode-brand-secondary p-ds-lg text-left dark:bg-color-bg-darkmode-brand-secondary">
                    <InfoCircle className="h-5 w-5 shrink-0 text-color-text-brand" />
                    <p className="typography-product-body-highlight text-color-text-brand">
                      We will decline all other requests for this game once you accept.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <footer className="flex shrink-0 flex-col gap-ds-md border-t border-color-border-input-lightmode px-ds-lg pb-ds-lg pt-ds-lg dark:border-color-border-input-darkmode lg:flex-row lg:gap-ds-xl lg:px-ds-3xl lg:py-5">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => setStep('review')}
                className="lg:min-w-0 lg:flex-1"
              >
                Go back
              </Button>
              <Button
                variant="brand"
                size="lg"
                onClick={handleAccept}
                loading={isAccepting}
                className="lg:min-w-0 lg:flex-1"
              >
                Accept
              </Button>
            </footer>
          </>
        )}

        {step === 'decline' && (
          <>
            <header className="flex shrink-0 items-center justify-end p-ds-lg lg:px-ds-3xl lg:py-5">
              <CloseButton onClose={handleClose} />
            </header>
            <div className="flex min-h-0 flex-1 flex-col gap-[5rem] overflow-y-auto px-ds-lg pb-ds-lg pt-ds-sm lg:gap-ds-2xl lg:px-ds-3xl lg:pb-16">
              <div className="flex flex-col items-center gap-ds-lg text-center lg:gap-ds-2xl">
                <Warning3dIllustration className="h-[7.5rem] w-[7.5rem]" />
                <h2 className="typography-product-display text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  Decline request
                </h2>
                <p className="typography-product-body max-w-[19.5rem] text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary lg:max-w-none">
                  The player will receive a notification that their request wasn&rsquo;t accepted.
                </p>
              </div>
              <div className="flex flex-col gap-ds-md">
                <label
                  htmlFor="decline-message"
                  className="typography-product-heading-compact-mobile lg:typography-product-heading-compact-desktop text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary lg:text-color-text-lightmode-primary lg:dark:text-color-text-darkmode-primary"
                >
                  Automatic response (edit if you wish)
                </label>
                <textarea
                  id="decline-message"
                  value={declineMessage}
                  onChange={(event) => setDeclineMessage(event.target.value)}
                  rows={3}
                  maxLength={MAX_PLAY_REQUEST_MESSAGE_LENGTH}
                  className="input-form"
                />
              </div>
            </div>
            <footer className="flex shrink-0 flex-col gap-ds-md border-t border-color-border-input-lightmode px-ds-lg pb-ds-lg pt-ds-lg dark:border-color-border-input-darkmode lg:flex-row lg:gap-ds-xl lg:px-ds-3xl lg:py-5">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => setStep('review')}
                className="lg:min-w-0 lg:flex-1"
              >
                Go back
              </Button>
              <Button
                variant="primary"
                size="lg"
                onClick={handleDecline}
                loading={isDeclining}
                className="lg:min-w-0 lg:flex-1"
              >
                Decline
              </Button>
            </footer>
          </>
        )}

        {step === 'done' && (
          <>
            <header className="flex shrink-0 items-center justify-end p-ds-lg lg:px-ds-3xl lg:py-5">
              <CloseButton onClose={handleClose} />
            </header>
            <div className="flex min-h-0 flex-1 flex-col items-center gap-ds-lg overflow-y-auto px-ds-lg pb-ds-2xl pt-ds-sm text-center lg:gap-ds-2xl lg:px-ds-3xl lg:pb-16">
              <Success3dIllustration className="h-[7.5rem] w-[7.5rem]" />
              <h2 className="typography-product-display text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Done!
              </h2>
            </div>
            {conversationId && (
              <footer className="flex shrink-0 flex-col gap-ds-md border-t border-color-border-input-lightmode px-ds-lg pb-ds-lg pt-ds-lg dark:border-color-border-input-darkmode lg:px-ds-3xl lg:py-5">
                <Button
                  variant="brand"
                  size="lg"
                  onClick={() => {
                    handleClose();
                    router.push(getPlayerChatPageUrl(conversationId));
                  }}
                >
                  Go to chat
                </Button>
              </footer>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
